if (!customElements.get('product-bars')) {
  class ProductBars extends HTMLElement {
    static selectors = {
      item: '[js-item]',
      currentTitle: '[js-current-title]',
      barsJson: '[data-bars-json]',
      mediaSlider: '.product__media-slider',
      quantity: {
        increase: '[name="increment"]',
        decrease: '[name="decrement"]',
      },
      singleTitle: '[js-title-single]',
      manyTitle: '[js-title-many]',
    };
    static classes = {
      itemHover: 'border-dashed-circle',
      active: 'is-active',
      atLimit: 'at-limit',
      mediaStackActive: 'product-bars__media-stack-active',
    };

    constructor() {
      super();

      this.limit = Number(this.getAttribute('js-limit'));
      this.selected = [];
      this.stackState = { expandedIds: [], heightPx: null };

      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleResize = this.handleResize.bind(this);
    }

    handleResize() {
      if (this.limit > 1) this.updateMediaStackHeight();
    }

    updateMediaStackHeight() {
      const container = this.ensureMediaStackContainer();
      if (!container) return;

      const heightPx = this.measureMediaHeightPx();
      if (!heightPx) return;

      container.style.height = `${heightPx}px`;
      this.stackState.heightPx = heightPx;
    }

    static get observedAttributes() {
      return ['js-limit'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue == newValue) return;
      if (name == 'js-limit') this.updateLimitAndSelected(Number(newValue));
    }

    applyLimitPageClasses() {
      const section = this.closest('.product');
      if (section) {
        section.classList.toggle('product--bars-limit-gt1', this.limit > 1);
      }

      const header = document.querySelector('header.header');
      if (header) {
        header.classList.toggle('is-transparent', this.limit > 1);
        header.classList.toggle('is-dark', this.limit > 1);
      }
    }

    parseBarsData() {
      const jsonEl = this.querySelector(ProductBars.selectors.barsJson);
      if (!jsonEl) return;

      const data = JSON.parse(jsonEl.textContent);
      this.defaultProduct = { ...data.default };
      this.barsById = new Map(data.bars.map((bar) => [String(bar.id), bar]));
    }

    ensureMediaStackContainer() {
      if (!this.productMedia) return null;

      if (!this.mediaStackEl) {
        this.mediaStackEl = this.productMedia.querySelector(
          '[js-bars-media-stack]',
        );
      }

      return this.mediaStackEl;
    }

    ensureMediaStackInner() {
      const outer = this.ensureMediaStackContainer();
      if (!outer) return null;

      if (!this.mediaStackInnerEl) {
        this.mediaStackInnerEl = outer.querySelector(
          '[js-bars-media-stack-inner]',
        );
      }

      return this.mediaStackInnerEl;
    }

    setMediaStackMode(isActive) {
      if (!this.productMedia) return;
      this.productMedia.classList.toggle(
        ProductBars.classes.mediaStackActive,
        Boolean(isActive),
      );
    }

    buildExpandedSelectionIds() {
      const expanded = [];
      this.selected.forEach((item) => {
        const id = String(item.id);
        const count = Number(item.count) || 0;
        for (let i = 0; i < count; i += 1) expanded.push(id);
      });
      return expanded;
    }

    getBarMainImageUrl(barId) {
      if (!barId) return null;
      const bar = this.barsById?.get(String(barId));
      return bar?.mainImage || null;
    }

    getDefaultStackBarId() {
      const firstBar = this.barsById?.values().next().value;
      return firstBar ? String(firstBar.id) : null;
    }

    isSameBarMedia(barId) {
      if (!barId || !this.productMedia) return false;

      const domBarId = this.productMedia.dataset.mediaBarId;
      return domBarId != null && String(domBarId) === String(barId);
    }

    isSameBarReviews(barId) {
      if (!barId || this.currentReviewBarId == null) return false;

      return String(this.currentReviewBarId) === String(barId);
    }

    syncCurrentMediaBarIdFromDom() {
      const domBarId = this.productMedia?.dataset?.mediaBarId;
      if (domBarId) this.currentMediaBarId = String(domBarId);
    }

    syncBundleMediaVisibility() {
      if (!this.productMedia) return;

      const hide = this.limit > 1;
      this.productMedia
        .querySelectorAll(
          ':scope > .product__image, :scope > .product__media-slider, :scope > .product__media-thumbs, :scope > svg',
        )
        .forEach((el) => {
          if (hide) {
            el.setAttribute('aria-hidden', 'true');
            el.style.opacity = '0';
            el.style.pointerEvents = 'none';
            el.style.visibility = 'hidden';
          } else {
            el.removeAttribute('aria-hidden');
            el.style.removeProperty('opacity');
            el.style.removeProperty('pointer-events');
            el.style.removeProperty('visibility');
          }
        });
    }

    measureMediaHeightPx() {
      if (!this.productMedia) return null;

      const rect = this.productMedia.getBoundingClientRect();
      if (!rect?.height) return null;

      return Math.round(rect.height);
    }

    clearMediaStack() {
      const inner = this.ensureMediaStackInner();
      inner?.replaceChildren();
      this.stackState.signature = null;
    }

    renderMediaStack() {
      const shouldStack = this.limit > 1;
      this.setMediaStackMode(shouldStack);
      this.syncBundleMediaVisibility();

      if (!shouldStack) {
        this.clearMediaStack();
        return;
      }

      const container = this.ensureMediaStackContainer();
      const inner = this.ensureMediaStackInner();
      if (!container || !inner) return;

      const expandedIds = this.buildExpandedSelectionIds();
      const effectiveIds = expandedIds.length
        ? expandedIds
        : [this.getDefaultStackBarId()].filter(Boolean);

      const heightPx = this.measureMediaHeightPx();
      if (heightPx) {
        container.style.height = `${heightPx}px`;
        this.stackState.heightPx = heightPx;
      } else if (this.stackState.heightPx != null) {
        container.style.height = `${this.stackState.heightPx}px`;
      }

      container.style.setProperty('--stack-count', String(effectiveIds.length));

      const signature = JSON.stringify(effectiveIds);
      if (this.stackState.signature === signature && inner.childElementCount)
        return;
      this.stackState.signature = signature;

      inner.replaceChildren();

      effectiveIds.forEach((barId, index) => {
        const url = this.getBarMainImageUrl(barId);
        if (!url) return;

        const img = document.createElement('img');
        img.alt = '';
        img.loading = index < 2 ? 'eager' : 'lazy';
        img.decoding = 'async';
        img.src = url;
        img.style.setProperty('--stack-index', String(index));
        img.style.zIndex = String(index + 1); // first selected is lowest z
        inner.appendChild(img);
      });
    }

    syncDefaultFromCurrentVariant() {
      if (!this.defaultProduct) return;

      const form = this.closest('form');
      const variantId = form?.elements['id']?.value;
      if (!variantId) return;

      const pricesEl = form?.querySelector('[data-variants-prices]');
      const variantsEl = form?.querySelector('[data-variants-json]');
      if (!pricesEl || !variantsEl) return;

      const prices = JSON.parse(pricesEl.textContent);
      const variants = JSON.parse(variantsEl.textContent);
      const priceObj = prices.find(
        (entry) => String(entry.id) === String(variantId),
      );
      const variant = variants.find(
        (entry) => String(entry.id) === String(variantId),
      );

      if (priceObj?.price_single != null) {
        this.defaultProduct.priceSingle = priceObj.price_single;
      }
    }

    cacheProductElements() {
      const section = this.closest('.product');
      if (!section) return;

      this.productTitle = section.querySelector('.product__title');
      this.productDescription = section.querySelector('.product__description');
      this.productAllergens = section.querySelector('.product__allergens');
      this.productPrice = section.querySelector('product-price');
      this.productMedia = section.querySelector('product-media');
    }

    getCurrentVariantId() {
      if (this.currentVariantId != null) return String(this.currentVariantId);

      return this.closest('form')?.elements['id']?.value;
    }

    setActiveDisplaySource(container, variantId, barId) {
      if (!container?.hasAttribute('js-product-display-container')) return;

      const sources = container.querySelectorAll('[js-product-display]');

      sources.forEach((el) => {
        el.classList.add('hidden');
        el.setAttribute('hidden', '');
      });

      const active =
        container.querySelector(
          `[js-product-display="variant"][data-variant-id="${variantId}"]`,
        ) ||
        container.querySelector(
          `[js-product-display="bar"][data-bar-id="${barId}"]`,
        ) ||
        container.querySelector('[js-product-display="default"]');

      if (!active) return;

      active.classList.remove('hidden');
      active.removeAttribute('hidden');
    }

    getActiveBarId() {
      if (this.selected.length === 0) return null;

      return this.selected[this.selected.length - 1].id;
    }

    getProductDataForDisplay() {
      const activeId = this.getActiveBarId();

      if (!activeId) return this.defaultProduct;

      return this.barsById.get(String(activeId)) || this.defaultProduct;
    }

    updateProductPrice(priceSingle) {
      if (priceSingle == null || !this.productPrice) return;

      document.dispatchEvent(
        new CustomEvent('update:price', {
          detail: { price: priceSingle, quantity: 1 },
        }),
      );
    }

    handleParsedMedia(parsedHTML, barId) {
      const media = parsedHTML.querySelector('product-media');
      if (!media) return;

      this.productMedia.replaceContent(
        media.innerHTML,
        media.dataset.realSlideCount,
      );
      this.mediaStackEl = null;
      this.mediaStackInnerEl = null;

      if (barId != null) {
        const barIdStr = String(barId);
        this.currentMediaBarId = barIdStr;
        this.productMedia.dataset.mediaBarId = barIdStr;
      }

      this.renderMediaStack();
    }

    captureOptionSelection(root) {
      const selection = {};
      if (!root) return selection;

      root
        .querySelectorAll('input[type="radio"]:checked, select')
        .forEach((el) => {
          if (el.dataset.name) selection[el.dataset.name] = el.value;
        });

      return selection;
    }

    applyOptionSelection(root, selection) {
      if (!root) return;

      Object.entries(selection).forEach(([name, value]) => {
        const select = root.querySelector(`select[data-name="${name}"]`);
        if (select) {
          if (
            Array.from(select.options).some((option) => option.value === value)
          ) {
            select.value = value;
          }
          return;
        }

        const radios = Array.from(
          root.querySelectorAll(`input[type="radio"][data-name="${name}"]`),
        );
        const match = radios.find((radio) => radio.value === value);
        if (!match) return;

        // Set state directly (no change event) so the bars' own change handler
        // isn't triggered; the product selector reads the checked state on
        // refresh(). Sync the custom dropdown's label/value manually since that
        // normally happens on a change event.
        radios.forEach((radio) => (radio.checked = radio === match));

        const dropdown = match.closest('product-dropdown');
        if (dropdown) {
          const label = dropdown.querySelector('[data-label]');
          if (label) label.textContent = value;
          const propertyInput = dropdown.querySelector('[js-dropdown-input]');
          if (propertyInput) propertyInput.value = value;
        }
      });
    }

    handleParsedSelector(parsedHTML, barId) {
      // Only relevant when switching between sibling products (e.g. t-shirt
      // colours). In bar media mode the bars are bundle components and the host
      // product's variant selector must stay put, so skip the swap entirely.
      if (this._inBarMediaMode) return;

      // Only apply if this bar is still the active selection — guards against
      // out-of-order responses when colours are switched quickly.
      if (barId != null && String(this.getActiveBarId()) !== String(barId)) {
        return;
      }

      const form = this.closest('form');
      if (!form) return;

      // Swap the variant option inputs (e.g. Size) for the switched product.
      const newSelector = parsedHTML.querySelector('[js-variant-selector]');
      const currentSelector = form.querySelector('[js-variant-selector]');
      if (newSelector && currentSelector) {
        // Carry the shopper's current option choice (e.g. Size) over to the
        // switched product when that value also exists for it; otherwise its
        // server-rendered default stays selected.
        this.applyOptionSelection(
          newSelector,
          this.captureOptionSelection(currentSelector),
        );
        currentSelector.replaceWith(newSelector);
      } else if (currentSelector && !newSelector) {
        currentSelector.innerHTML = '';
      }

      // Swap the variant + price data the selector resolves against, then let
      // the product selector re-read it and re-resolve the current variant.
      ['[data-variants-json]', '[data-variants-prices]'].forEach((selector) => {
        const incoming = parsedHTML.querySelector(selector);
        const current = form.querySelector(selector);
        if (incoming && current) current.textContent = incoming.textContent;
      });

      const mainSelector = this.closest('.product')?.querySelector(
        'main-product-selector',
      );
      if (mainSelector && typeof mainSelector.refresh === 'function') {
        mainSelector.refresh();
      }
    }

    handleParsedReviews(parsedHTML) {
      const reviews = parsedHTML.querySelector('.junip-product-summary');
      if (!reviews) return;

      const current = document.querySelector('.junip-product-summary');
      if (!reviews || !current) return;

      const summary = reviews.cloneNode(true);
      summary.addEventListener('click', () => {
        document.querySelector('.junip-product-review')?.scrollIntoView({
          behavior: 'smooth',
        });
      });
      current.replaceWith(summary);
    }

    handleParsedProductReview(parsedHTML) {
      const incoming = parsedHTML.querySelectorAll('.junip-product-review');
      const current = document.querySelectorAll('.junip-product-review');

      current.forEach((widget, index) => {
        const fresh = incoming[index];
        if (fresh) widget.replaceWith(fresh.cloneNode(true));
      });
    }

    fetchBarProductReview(bar) {
      const sectionId = document
        .querySelector('.junip-product-review')
        ?.closest('[id^="shopify-section-"]')
        ?.id.replace('shopify-section-', '');

      if (!sectionId) return;

      return fetch(`/products/${bar.handle}?section_id=${sectionId}`)
        .then((response) => response.text())
        .then((html) => {
          const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
          this.handleParsedProductReview(parsedHTML);
        });
    }

    fetchBarMedia(barId) {
      const barIdStr = String(barId);
      const bar = this.barsById.get(barIdStr);
      if (!bar?.handle) return;

      const needsMedia =
        Boolean(this.productMedia?.replaceContent) &&
        !this.isSameBarMedia(barIdStr) &&
        this._mediaFetchBarId !== barIdStr;
      const needsReviews =
        !this.isSameBarReviews(barIdStr) && this._reviewFetchBarId !== barIdStr;
      if (!needsMedia && !needsReviews) return;

      const sectionId = this.closest('.product')?.dataset.section;
      if (!sectionId) return;

      // Mark this bar's fetch as in-flight so overlapping calls in the same
      // frame don't kick off a second request and re-hydrate Junip twice.
      if (needsMedia) this._mediaFetchBarId = barIdStr;
      if (needsReviews) this._reviewFetchBarId = barIdStr;

      // Disable Add to Cart while switching to a different product; refresh()
      // restores the correct availability-based state once it's in place. The
      // initial hydration fetch (same product already shown) is skipped.
      const isSwitch =
        this._displayedBarId != null && barIdStr !== this._displayedBarId;
      this._displayedBarId = barIdStr;
      if (isSwitch) this.setSubmitDisabled(true);

      fetch(`/products/${bar.handle}?section_id=${sectionId}`)
        .then((response) => response.text())
        .then((html) => {
          const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

          if (needsMedia) this.handleParsedMedia(parsedHTML, barIdStr);
          if (needsReviews) {
            this.handleParsedReviews(parsedHTML);
            this.currentReviewBarId = barIdStr;
          }
          this.handleParsedSelector(parsedHTML, barIdStr);
        })
        .then(() => {
          if (needsReviews) return this.fetchBarProductReview(bar);
        })
        .catch(() => {
          if (isSwitch) this.setSubmitDisabled(false);
        })
        .finally(() => {
          if (needsMedia && this._mediaFetchBarId === barIdStr) {
            this._mediaFetchBarId = null;
          }
          if (needsReviews && this._reviewFetchBarId === barIdStr) {
            this._reviewFetchBarId = null;
          }
        });
    }

    setSubmitDisabled(disabled) {
      const submitButton =
        this.closest('form')?.querySelector('[type="submit"]');
      if (submitButton) submitButton.toggleAttribute('disabled', disabled);
    }

    updateProductMedia(barId) {
      if (this.limit !== 1 || !barId) return;
      if (this.isSameBarMedia(barId) && this.isSameBarReviews(barId)) return;

      this.fetchBarMedia(barId);
    }

    updateActiveSelectedPanel(activeId) {
      const selectedIds = new Set(this.selected.map((item) => String(item.id)));
      this.querySelectorAll('[js-selected-id]').forEach((element) => {
        element.classList.toggle(
          ProductBars.classes.active,
          selectedIds.has(String(element.getAttribute('js-selected-id'))),
        );
      });
    }

    updateCurrentTitleFromSelection() {
      if (!this.currentTitle) return;

      const activeId = this.getActiveBarId();

      if (!activeId) {
        this.currentTitle.innerHTML = this.initialCurrentTitle;
        return;
      }

      const bar = this.barsById.get(String(activeId));
      if (bar?.title) this.currentTitle.innerHTML = bar.title;
    }

    applyLimitOneCart() {
      if (this.limit === 1) {
        const activeId = this.getActiveBarId();
        const bar = activeId ? this.barsById.get(String(activeId)) : null;

        // When switching between sibling products that have their own variant
        // selector (e.g. t-shirt colours with a Size option), that selector owns
        // the cart line's variant id so the shopper's choice isn't overwritten
        // with the product's default variant. In bar media mode the variant
        // selector belongs to the bundle host, not the bar, so the cart line
        // must still become the selected bar's product.
        const selectorOwnsVariant =
          !this._inBarMediaMode &&
          Boolean(
            this.closest('form')?.querySelector(
              '[js-variant-selector] input, [js-variant-selector] select',
            ),
          );

        if (!selectorOwnsVariant && bar?.variantId && this.cartIdInput) {
          this.cartIdInput.value = bar.variantId;
        }

        if (this.barsInput) this.barsInput.disabled = true;

        if (bar) {
          if (this._lastUrlBarId == null) {
            this._lastUrlBarId = bar.id;
          } else if (String(bar.id) !== String(this._lastUrlBarId)) {
            if (bar.url) history.replaceState({}, '', bar.url);
            this._lastUrlBarId = bar.id;
          }
        }

        return;
      }

      if (this.cartIdInput) {
        this.cartIdInput.value = this.currentVariantId ?? this.pageVariantId;
      }
      if (this.barsInput) this.barsInput.disabled = false;
    }

    updateProductDisplay() {
      const data = this.getProductDataForDisplay();
      const variantId = this.getCurrentVariantId();
      const barId = this.getActiveBarId();

      if (this.productTitle?.hasAttribute('js-product-display-container')) {
        this.setActiveDisplaySource(this.productTitle, variantId, barId);
      } else if (this.productTitle && data?.title) {
        this.productTitle.innerHTML = data.title;
      }

      if (
        this.productDescription?.hasAttribute('js-product-display-container')
      ) {
        this.setActiveDisplaySource(this.productDescription, variantId, barId);
      } else if (this.productDescription) {
        this.productDescription.innerHTML = data?.description || '';
      }

      if (this.productAllergens?.hasAttribute('js-product-display-container')) {
        this.setActiveDisplaySource(this.productAllergens, variantId, barId);
      }

      if (!data) return;

      this.updateProductMedia(barId);
      this.updateActiveSelectedPanel(barId);
      this.updateCurrentTitleFromSelection();
      this.applyLimitOneCart();
    }

    updateDefaultFromVariant(event) {
      if (!this.defaultProduct || !event.detail) return;

      if (event.detail.price != null) {
        this.defaultProduct.priceSingle = event.detail.price;
        this.updateProductPrice(event.detail.price);
      }
    }

    updateFormActions() {
      const count = this.getTotalCount();

      const submitButton =
        this.closest('form').querySelector('[type="submit"]');
      if (!submitButton) return;

      submitButton.toggleAttribute('disabled', count < this.limit);

      // The [js-note] element is the bundle progress message ("add X more").
      // It's only meaningful for multi-select bundles; at limit 1 (t-shirt
      // colours, box selector) leave the buy-buttons note untouched instead of
      // clobbering it with an empty string.
      if (this.limit <= 1) return;

      const note = this.closest('form').querySelector('[js-note]');
      if (!note) return;

      const attribute =
        count < this.limit ? 'js-text-note' : 'js-text-note-ready';
      note.innerHTML = this.getAttribute(attribute).replace(
        '{X}',
        this.limit - count,
      );
    }

    updateState() {
      requestAnimationFrame(() => {
        this.updateSelectedOrder();
        this.updateHiddenInput();
        this.updateLimitElements();
        this.updateTextElements();
        this.updateProductDisplay();
        this.updateFormActions();
        this.renderMediaStack();
        this.updateBoxTracker();
      });
    }

    // Box-selector presentation (an alternative to the image swatches): the
    // sliding highlight + prev/next arrows. Mirrors the standalone
    // box-selector element but lives here so a single controller owns the
    // radios — avoids a change-listener ordering race between two components.
    initBoxSelector() {
      this.boxTracker = this.querySelector('[data-tracker]');
      this.boxNav = this.querySelector('[data-nav]');
      this.boxPrevBtn = this.boxNav?.querySelector('[data-button-prev]');
      this.boxNextBtn = this.boxNav?.querySelector('[data-button-next]');

      requestAnimationFrame(() => {
        if (this.boxTracker) this.boxTracker.style.transition = 'none';
        if (this.boxNav) this.boxNav.style.transition = 'none';
        this.updateBoxTracker();

        requestAnimationFrame(() => {
          if (this.boxTracker) this.boxTracker.style.transition = '';
          if (this.boxNav) this.boxNav.style.transition = '';
        });
      });

      this.boxResizeObserver = new ResizeObserver(() =>
        this.updateBoxTracker(),
      );
      this.boxResizeObserver.observe(this.boxItemsWrapper);

      this.boxPrevBtn?.addEventListener('click', () =>
        this.navigateBox('previous'),
      );
      this.boxNextBtn?.addEventListener('click', () =>
        this.navigateBox('next'),
      );
    }

    updateBoxTracker() {
      if (!this.boxItemsWrapper || !this.boxTracker) return;

      const input = this.boxItemsWrapper.querySelector('input:checked');
      if (!input) return;

      const label = input.nextElementSibling;
      if (!label) return;

      const wrapperRect = this.boxItemsWrapper.getBoundingClientRect();
      const labelRect = label.getBoundingClientRect();
      const labelCenter =
        labelRect.left - wrapperRect.left + labelRect.width / 2;
      const offsetLeft = labelCenter - this.boxTracker.offsetWidth / 2;

      this.boxTracker.style.transform = 'translateX(' + offsetLeft + 'px)';
      if (this.boxNav) {
        this.boxNav.style.transform = 'translateX(' + offsetLeft + 'px)';
      }

      const li = input.closest('li');
      if (this.boxPrevBtn) {
        this.boxPrevBtn.disabled = !li?.previousElementSibling;
      }
      if (this.boxNextBtn) {
        this.boxNextBtn.disabled = !li?.nextElementSibling;
      }
    }

    navigateBox(direction) {
      const input = this.boxItemsWrapper?.querySelector('input:checked');
      const sibling = input?.closest('li')?.[direction + 'ElementSibling'];
      if (!sibling) return;

      const nextInput = sibling.querySelector('input[type="radio"]');
      if (!nextInput) return;

      nextInput.checked = true;
      nextInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    trimSelectedToNewLimit(overLimitCount) {
      if (overLimitCount <= 0) return;

      while (overLimitCount > 0 && this.selected.length > 0) {
        const oldest = this.selected[0];
        overLimitCount -= oldest.count;

        this.removeSelected(oldest.id);

        const checkbox = this.querySelector(`[js-id="${oldest.id}"]`);
        if (checkbox) checkbox.checked = false;
      }
    }

    updateLimitAndSelected(newLimit) {
      const oldLimit = this.limit;
      this.limit = newLimit;
      this.applyLimitPageClasses();

      const currentCount = this.getTotalCount();

      if (newLimit < oldLimit && currentCount > newLimit) {
        this.trimSelectedToNewLimit(currentCount - newLimit);
      }

      this.updateState();
    }

    updateHiddenInput() {
      const input = this.querySelector('[js-bars-input]');
      if (!input) return;

      input.value = this.selected
        .map((item) => {
          return `${item.count} x ${item.title}`;
        })
        .join('\n');
    }

    updateCurrentTitle(item) {
      const titleText = item.getAttribute('js-item-title');
      if (!titleText) return;

      this.currentTitle.innerHTML = titleText;
    }

    handleMouseOver(event) {
      const item = event.target.closest(ProductBars.selectors.item);
      if (!item) return;

      this.updateCurrentTitle(item);

      if (item.querySelector('[type="checkbox"]')?.checked) return;
      item.classList.add(ProductBars.classes.itemHover);
    }

    handleMouseOut(event) {
      const item = event.target.closest(ProductBars.selectors.item);
      if (!item) return;

      item.classList.remove(ProductBars.classes.itemHover);
      this.updateCurrentTitleFromSelection();
    }

    getSelectedItemById(id) {
      return this.querySelector(`[js-selected-id="${id}"]`);
    }

    findSelectedById(id) {
      return this.selected.find((item) => String(item.id) === String(id));
    }

    updateSelectedOrder() {
      this.selected.forEach((item, index) => {
        const selectedItem = this.getSelectedItemById(item.id);
        if (selectedItem) {
          selectedItem.style.setProperty('--order', index);
        }
      });
    }

    updateSelected(id, count, title) {
      const numericCount = Number(count) || 0;
      const existing = this.findSelectedById(id);
      if (existing) {
        existing.count = numericCount;
      } else {
        this.selected.push({ id: String(id), count: numericCount, title });
      }

      this.updateState();
    }

    removeSelected(id) {
      const index = this.selected.findIndex(
        (item) => String(item.id) === String(id),
      );
      if (index == -1) return;

      this.selected.splice(index, 1);

      const selected = this.getSelectedItemById(id);
      if (!selected) return;
      selected.classList.remove(ProductBars.classes.active);
      selected.style.removeProperty('--order');

      const quantityInput = selected.querySelector('input[js-quantity-id]');
      if (quantityInput) quantityInput.value = 1;

      this.updateState();
    }

    getTotalCount() {
      return this.selected.reduce(
        (sum, item) => sum + (Number(item.count) || 0),
        0,
      );
    }

    updateMainTitle() {
      if (!this.singleTitle) return;
      this.singleTitle.classList.toggle('hidden', this.limit > 1);

      if (!this.manyTitle) return;
      this.manyTitle.classList.toggle('hidden', this.limit == 1);
      this.manyTitle.innerHTML = this.manyTitle
        .getAttribute('js-title-many')
        .replace('{X}', this.limit);
    }

    updateTextElements() {
      this.updateMainTitle();
    }

    updateLimitElements() {
      const count = this.getTotalCount();
      const isAtLimit = count >= this.limit;

      this.classList.toggle(ProductBars.classes.atLimit, isAtLimit);

      this.querySelectorAll(ProductBars.selectors.quantity.increase).forEach(
        (button) => {
          button.toggleAttribute('disabled', isAtLimit);
        },
      );
    }

    handleAddSelected(checkbox) {
      const id = checkbox.getAttribute('js-id');

      if (this.getTotalCount() >= this.limit) {
        const oldestId = this.selected[0].id;
        this.removeSelected(oldestId);

        const oldestCheckbox = this.querySelector(`[js-id="${oldestId}"]`);
        if (oldestCheckbox) {
          oldestCheckbox.checked = false;
        }

        const oldestSelected = this.getSelectedItemById(oldestId);
        if (oldestSelected) {
          oldestSelected.classList.remove(ProductBars.classes.active);
        }
      }

      const label =
        checkbox.closest('label') ||
        this.querySelector(`label[for="${checkbox.id}"]`);
      const title = label ? label.getAttribute('js-item-title') : id;

      this.updateSelected(id, 1, title);

      const selected = this.getSelectedItemById(id);
      if (!selected) return;
      selected.classList.add(ProductBars.classes.active);

      const quantityInput = selected.querySelector('input[js-quantity-id]');
      if (quantityInput && quantityInput.value < 1) {
        quantityInput.value = 1;
      }
    }

    handleRemoveSelected(checkbox) {
      const id = checkbox.getAttribute('js-id');
      this.removeSelected(id);
    }

    handleUpdateQuantity(event) {
      const id = event.target.getAttribute('js-quantity-id');
      if (!id) return;

      let count = Number(event.target.value);

      if (count !== 0) {
        const existing = this.findSelectedById(id);
        const otherCount =
          this.getTotalCount() - (existing ? Number(existing.count) || 0 : 0);
        const maxAllowed = Math.max(this.limit - otherCount, 1);

        if (count > maxAllowed) {
          count = maxAllowed;
          event.target.value = count;
        }

        this.updateSelected(id, count, existing?.title);
      } else {
        const checkbox = this.querySelector(`[js-id="${id}"]`);
        checkbox.checked = false;
        this.removeSelected(id);
      }
    }

    handleChange(event) {
      if (event.target.getAttribute('type') == 'number')
        return this.handleUpdateQuantity(event);

      if (event.target.checked) {
        const item = event.target.closest(ProductBars.selectors.item);
        if (item) item.classList.remove(ProductBars.classes.itemHover);

        return this.handleAddSelected(event.target);
      }

      this.handleRemoveSelected(event.target);
    }

    handleVariantChange(event) {
      if (event.detail?.variant?.id != null) {
        this.currentVariantId = event.detail.variant.id;
      }

      this.updateDefaultFromVariant(event);

      if (event.detail.bars?.limit != null) {
        this.setAttribute('js-limit', event.detail.bars.limit);
      }

      this.updateProductDisplay();
      requestAnimationFrame(() => this.applyLimitOneCart());
    }

    removeBarsProductIdFromUrl() {
      const params = new URLSearchParams(window.location.search);
      if (!params.has('bars_product_id')) return;

      params.delete('bars_product_id');
      const query = params.toString();
      const url = `${window.location.pathname}${query ? `?${query}` : ''}${
        window.location.hash
      }`;
      history.replaceState({}, '', url);
    }

    applyBarsProductIdFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const barProductId = params.get('bars_product_id');
      if (!barProductId) return null;

      if (this.barsById?.has(String(barProductId))) {
        const checkbox = this.querySelector(`[js-id="${barProductId}"]`);
        if (checkbox) {
          this.querySelectorAll('[js-id]:checked').forEach((cb) => {
            cb.checked = false;
          });
          this.selected = [];
          checkbox.checked = true;
        }
      }

      return barProductId;
    }

    disconnectedCallback() {
      document.removeEventListener('variant:change', this.handleVariantChange);
      window.removeEventListener('resize', this.handleResize);
      this.boxResizeObserver?.disconnect();
    }

    connectedCallback() {
      this.parseBarsData();
      // Capture the server-rendered active bar (the product whose media/reviews
      // are already in the DOM) before the URL param can reassign the selection.
      const serverActiveBarId =
        this.querySelector('[js-id]:checked')?.getAttribute('js-id');
      const urlBarProductId = this.applyBarsProductIdFromUrl();
      this.cacheProductElements();
      this.syncCurrentMediaBarIdFromDom();
      this.applyLimitPageClasses();
      this.currentVariantId = this.closest('form')?.elements['id']?.value;
      this.cartIdInput = this.closest('form')?.elements['id'];
      this.barsInput = this.querySelector('[js-bars-input]');
      this.pageVariantId = this.cartIdInput?.value;
      this.syncDefaultFromCurrentVariant();

      this.items = this.querySelectorAll(ProductBars.selectors.item);
      this.currentTitle = this.querySelector(
        ProductBars.selectors.currentTitle,
      );
      this.initialCurrentTitle = this.currentTitle?.innerHTML ?? '';
      this.singleTitle = this.querySelector(ProductBars.selectors.singleTitle);
      this.manyTitle = this.querySelector(ProductBars.selectors.manyTitle);

      this.addEventListener('mouseover', this.handleMouseOver.bind(this));
      this.addEventListener('mouseout', this.handleMouseOut.bind(this));

      this.addEventListener('change', this.handleChange.bind(this));

      this.boxItemsWrapper = this.querySelector('[data-items-wrapper]');
      if (this.boxItemsWrapper) this.initBoxSelector();

      document.addEventListener('variant:change', this.handleVariantChange);
      window.addEventListener('resize', this.handleResize);

      // Mark the current (server-rendered) product's media and reviews as
      // current BEFORE dispatching the initial change events. Those events
      // bubble to the product selector and synchronously trigger a
      // variant:change -> handleVariantChange -> updateProductMedia, which would
      // otherwise fetch and re-render (flicker) media that's already on the page.
      // Also lets fetchBarMedia tell the initial state from an actual switch (so
      // Add to Cart isn't needlessly disabled on load). In bar media mode the
      // media belongs to a different product, so leave it to fetch as before; a
      // ?bars_product_id= deep link keeps the markers on the rendered product so
      // its switch still fetches.
      this._displayedBarId =
        serverActiveBarId != null ? String(serverActiveBarId) : null;

      // Bar media mode = the current product isn't one of the collection
      // products (e.g. the "build your bars" bundle host). There the bars are
      // components and the product's own variant selector (e.g. bundle size)
      // must be left alone, so the in-place selector swap is disabled.
      this._inBarMediaMode = Boolean(this.productMedia?.dataset.mediaBarId);
      if (this._displayedBarId != null && !this._inBarMediaMode) {
        if (this.productMedia) {
          this.productMedia.dataset.mediaBarId = this._displayedBarId;
        }
        this.currentReviewBarId = this._displayedBarId;
      }

      const checked = this.querySelectorAll('[js-id]:checked');
      checked.forEach((checkbox) =>
        checkbox.dispatchEvent(new Event('change', { bubbles: true })),
      );

      // Each dispatched change already schedules updateState() (display, form
      // actions, media stack). Only run them directly when nothing was checked,
      // otherwise we'd render — and fetch bar media/reviews — twice on load.
      if (!checked.length) {
        this.updateProductDisplay();
        this.updateFormActions();
        this.renderMediaStack();
      }

      if (urlBarProductId) {
        this.removeBarsProductIdFromUrl();
      }
    }
  }

  customElements.define('product-bars', ProductBars);
}
